// Parsers for carrier call-detail records from AT&T (plain-text legal-demand reports),
// Verizon (CSV exports named per subject number) and T-Mobile (CSV/XLSX exports).
// processBuckets({ att, verizon, tmobile }) takes file-like objects ({ name, text(),
// arrayBuffer() }) sorted by carrier and returns every record, tagged with its carrier and
// subject number, with duplicates flagged; T-Mobile timing-advance sessions come back
// separately as `timingAdvance`. Timestamps come back as the carrier's wall-clock
// time stored in a Date's UTC fields. Plain JavaScript, no React or DOM: used by the Phone
// Record Processing page and by the Case Analyzer desktop app.

// ── Verizon file classification ───────────────────────────────────────

export const VZW_FILE_KINDS = [
  { pattern: /cdr-4g[_-]volte/i,              kind: 'volte4g',      label: '4G VoLTE Voice CDR',      icon: '📞', skip: false },
  { pattern: /vce-5g.*voice|voice.*vce-5g/i,  kind: 'voice5g',      label: '5G Core Voice CDR',       icon: '📡', skip: false },
  { pattern: /cdr-billing/i,                   kind: 'billing',      label: 'Billing CDR (Voice)',      icon: '📞', skip: false },
  { pattern: /ip-tdr5/i,                       kind: 'data5g',       label: '5G Data Sessions',         icon: '🌐', skip: false },
  // BDR ("Internet activity") files report NAT port allocation, not byte
  // counts — a different schema per generation, so 5G must be checked
  // before the generic 4G pattern to get the right label (both are parsed
  // the same way; ip-bdr would otherwise also match the 5G filename first).
  { pattern: /ip-bdr.*5g|ip-bdr-5g/i,          kind: 'bdr5g',        label: '5G Data Sessions (BDR)',   icon: '🌐', skip: false },
  { pattern: /ip-bdr/i,                        kind: 'bdr4g',        label: '4G Data Sessions (BDR)',   icon: '🌐', skip: false },
  { pattern: /cdr-visible/i,                   kind: 'voltevisible', label: 'Visible CDR',              icon: '📞', skip: false },
  { pattern: /actdeact/i,                      kind: 'actdeact',     label: 'Activation/Deactivation',  icon: '🔄', skip: true  },
  { pattern: /deviceid/i,                      kind: 'deviceid',     label: 'Device ID / IMEI',         icon: '📱', skip: true  },
  { pattern: /features/i,                      kind: 'features',     label: 'Features',                 icon: '⚙️', skip: true  },
  { pattern: /othernumbers/i,                  kind: 'othernumbers', label: 'Other Numbers on Account', icon: '🔢', skip: true  },
  { pattern: /owneremail/i,                    kind: 'owneremail',   label: 'Owner Email',              icon: '✉️', skip: true  },
  { pattern: /paymentactivity/i,               kind: 'payment',      label: 'Payment Activity',         icon: '💳', skip: true  },
  { pattern: /^ssn__/i,                        kind: 'ssn',          label: 'SSN',                      icon: '🔒', skip: true  },
  { pattern: /subscriber__/i,                  kind: 'subscriber',   label: 'Subscriber Info',          icon: '👤', skip: true  },
  { pattern: /noresults|__noresults/i,         kind: 'noresults',    label: 'No Results',               icon: '—',  skip: true  },
  { pattern: /\.pdf$/i,                        kind: 'pdf',          label: 'PDF (skipped)',            icon: '📄', skip: true  },
  { pattern: /bdr.*\.xlsx?$|\.xlsx?.*bdr/i,    kind: 'bdr',          label: 'BDR Report',               icon: '📊', skip: true  },
];

function classifyVzwFile(file) {
  const name = file.name.toLowerCase();
  for (const def of VZW_FILE_KINDS) {
    if (def.pattern.test(name)) return { file, ...def };
  }
  return { file, kind: 'unknown', label: 'Unknown File', icon: '❓', skip: true };
}

// Verizon exports encode the queried subject number right in the filename,
// immediately after the report-type prefix's "__" — e.g.
// "CDR-Billing__2132380676_2025-01-01_to_2025-06-03.178.csv" or
// "...__MTN-2132380676_...". This is the authoritative source for which
// subject a file belongs to (each report is queried against one number),
// so multiple people's Verizon returns can be told apart when uploaded
// together — a "-many" suffix or multiple dash-joined numbers marks a
// batch lookup across several subjects at once, which can't be attributed
// to a single one and is treated as unknown.
function extractVzwFileNumber(fileName) {
  const m = String(fileName || '').match(/__(?:MTN-)?(\d{10})(-\d{10})*(-many)?[_.]/i);
  if (!m || m[2] || m[3]) return '';
  return m[1];
}

// ── Verizon parsing helpers ───────────────────────────────────────────

function parseTsVzw(raw) {
  if (!raw) return null;
  // Strip "(GMT +0)", "(GMT-5)", etc.
  const s = String(raw).replace(/\(GMT[^)]*\)/gi, '').trim();
  // YYYY/MM/DD HH:MM:SS
  const m1 = s.match(/(\d{4})\/(\d{2})\/(\d{2})\s+(\d{2}):(\d{2}):(\d{2})/);
  if (m1) return new Date(Date.UTC(+m1[1], +m1[2]-1, +m1[3], +m1[4], +m1[5], +m1[6]));
  // MM/DD/YYYY HH:MM:SS
  const m2 = s.match(/(\d{2})\/(\d{2})\/(\d{4})\s+(\d{2}):(\d{2}):(\d{2})/);
  if (m2) return new Date(Date.UTC(+m2[3], +m2[1]-1, +m2[2], +m2[4], +m2[5], +m2[6]));
  const d = new Date(s);
  if (isNaN(d.getTime())) return null;
  // Keep the printed wall-clock; store it in the Date's UTC fields.
  return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), d.getHours(), d.getMinutes(), d.getSeconds()));
}

function parseSipUri(raw) {
  if (!raw) return '';
  // Strip sip: or tel: prefix, then strip @domain suffix
  const s = String(raw).replace(/^(?:sip|tel):/i, '').split('@')[0];
  return normNum(s);
}

// Exact match first, in variant-priority order, THEN substring fallback —
// otherwise a short variant like 'date' or 'number' can match an earlier,
// unrelated header that merely contains it as a substring (e.g. "Bill
// Date" or "Account Number") before ever reaching the real "Date"/"Number"
// column.
function hIdx(headers, ...names) {
  const hls = headers.map(h => h.toLowerCase().trim());
  for (const name of names) {
    const nl = name.toLowerCase();
    const i = hls.findIndex(h => h === nl);
    if (i >= 0) return i;
  }
  for (const name of names) {
    const nl = name.toLowerCase();
    const i = hls.findIndex(h => h.includes(nl));
    if (i >= 0) return i;
  }
  return -1;
}

// ── CSV Parser ────────────────────────────────────────────────────────

function parseCSVText(text) {
  const lines = text.split(/\r?\n/);
  const rows = [];
  for (const line of lines) {
    if (!line.trim()) continue;
    const row = [];
    let inQ = false, cur = '';
    for (const ch of line) {
      if (ch === '"') inQ = !inQ;
      else if (ch === ',' && !inQ) { row.push(cur); cur = ''; }
      else cur += ch;
    }
    row.push(cur);
    rows.push(row.map(v => v.trim().replace(/^"|"$/g, '')));
  }
  return rows;
}

function findHeaderRow(parsed, minCols = 3) {
  for (let i = 0; i < Math.min(parsed.length, 10); i++) {
    if (parsed[i].filter(v => v).length >= minCols) return i;
  }
  return 0;
}

// ── Verizon per-file parsers ──────────────────────────────────────────

async function parseVzwVoLTE4G(file) {
  const parsed = parseCSVText(await file.text());
  if (parsed.length < 2) return { records: [], subNum: '' };
  const hi = findHeaderRow(parsed);
  const hdrs = parsed[hi].map(h => h.trim());
  const rows = parsed.slice(hi + 1).filter(r => r.some(c => c));

  const tsIdx   = hIdx(hdrs, 'record open date/time');
  const dirIdx  = hIdx(hdrs, 'dir');
  const msIdx   = hIdx(hdrs, 'msisdn');
  const callIdx = hIdx(hdrs, 'called #', 'called#');
  const cpnIdx  = hIdx(hdrs, 'cpn');
  const souIdx  = hIdx(hdrs, 'sou');

  const get = (row, idx) => idx >= 0 ? (row[idx] || '') : '';

  // Determine subNum from most-frequent MSISDN
  const freq = {};
  rows.forEach(r => { const n = normNum(get(r, msIdx)); if (n.length >= 10) freq[n] = (freq[n]||0)+1; });
  const subNum = Object.entries(freq).sort((a,b)=>b[1]-a[1])[0]?.[0] || '';

  const records = rows.map((row, idx) => {
    const ts      = parseTsVzw(get(row, tsIdx));
    const dirCode = get(row, dirIdx).toUpperCase().trim();
    const msisdn  = normNum(get(row, msIdx));
    const called  = normNum(get(row, callIdx));
    const cpn     = normNum(get(row, cpnIdx));
    const dur     = parseInt(get(row, souIdx), 10) || 0;

    let direction, contact;
    if (dirCode === 'MT') { direction = 'incoming';  contact = cpn || called; }
    else if (dirCode === 'MO') { direction = 'outgoing'; contact = called; }
    else {
      direction = called && called !== msisdn ? 'outgoing' : 'unknown';
      contact   = direction === 'outgoing' ? called : (cpn || called);
    }

    return {
      id: idx, source: 'VoLTE-4G', timestamp: ts, direction, type: 'voice',
      contactNumber: contact || '', durationSeconds: isNaN(dur) ? 0 : dur,
      cellId: '', imei: '',
      raw: Object.fromEntries(hdrs.map((h, j) => [h, row[j] || ''])),
    };
  }).filter(r => r.contactNumber || r.timestamp);

  return { records, subNum };
}

async function parseVzwVoice5G(file) {
  const parsed = parseCSVText(await file.text());
  if (parsed.length < 2) return { records: [], subNum: '' };
  const hi = findHeaderRow(parsed);
  const hdrs = parsed[hi].map(h => h.trim());
  const rows = parsed.slice(hi + 1).filter(r => r.some(c => c));

  const msIdx      = hIdx(hdrs, 'msisdn');
  const callingIdx = hIdx(hdrs, 'calling party no', 'calling party');
  const calledIdx  = hIdx(hdrs, 'called prty addr', 'called prty', 'called party');
  const tsIdx      = hIdx(hdrs, 'record open date/time(local)', 'record open date/time(gmt)', 'record open');
  const durIdx     = hIdx(hdrs, 'timeusage', 'duration');

  const get = (row, idx) => idx >= 0 ? (row[idx] || '') : '';

  const freq = {};
  rows.forEach(r => { const n = normNum(get(r, msIdx)); if (n.length >= 10) freq[n] = (freq[n]||0)+1; });
  const subNum = Object.entries(freq).sort((a,b)=>b[1]-a[1])[0]?.[0] || '';

  const records = rows.map((row, idx) => {
    const msisdn  = normNum(get(row, msIdx));
    const calling = parseSipUri(get(row, callingIdx));
    const called  = parseSipUri(get(row, calledIdx));
    const ts      = parseTsVzw(get(row, tsIdx));
    const dur     = parseInt(get(row, durIdx), 10) || 0;

    let direction, contact;
    if (calling && calling === (msisdn || subNum)) {
      direction = 'outgoing'; contact = called;
    } else if (calling) {
      direction = 'incoming'; contact = calling;
    } else {
      direction = 'unknown'; contact = called || calling;
    }

    return {
      id: idx, source: 'VCE-5G', timestamp: ts, direction, type: 'voice',
      contactNumber: contact || '', durationSeconds: isNaN(dur) ? 0 : dur,
      cellId: '', imei: '',
      raw: Object.fromEntries(hdrs.map((h, j) => [h, row[j] || ''])),
    };
  }).filter(r => r.contactNumber || r.timestamp);

  return { records, subNum };
}

async function parseVzwData(file, sourceLabel) {
  const parsed = parseCSVText(await file.text());
  if (parsed.length < 2) return { records: [], subNum: '' };
  const hi = findHeaderRow(parsed);
  const hdrs = parsed[hi].map(h => h.trim());
  const rows = parsed.slice(hi + 1).filter(r => r.some(c => c));

  const tsOpenIdx  = hIdx(hdrs, 'record open dt/tm', 'record open');
  const tsCloseIdx = hIdx(hdrs, 'record close dt/tm', 'record close');
  const msIdx      = hIdx(hdrs, 'msisdn', 'search mtn');
  const mbIdx      = hIdx(hdrs, 'orig total mb', 'total mb');
  const ipv4Idx    = hIdx(hdrs, 'pdu ipv4 address', 'ipv4');
  const ipv6Idx    = hIdx(hdrs, 'pdu ipv6 address', 'ipv6');

  const get = (row, idx) => idx >= 0 ? (row[idx] || '') : '';

  const freq = {};
  rows.forEach(r => { const n = normNum(get(r, msIdx)); if (n.length >= 10) freq[n] = (freq[n]||0)+1; });
  const subNum = Object.entries(freq).sort((a,b)=>b[1]-a[1])[0]?.[0] || '';

  const records = rows.map((row, idx) => {
    const tsOpen  = parseTsVzw(get(row, tsOpenIdx));
    const tsClose = parseTsVzw(get(row, tsCloseIdx));
    const dur = tsOpen && tsClose ? Math.round((tsClose - tsOpen) / 1000) : 0;
    const mb  = parseFloat(get(row, mbIdx)) || 0;
    const ip  = get(row, ipv4Idx) || get(row, ipv6Idx);

    return {
      id: idx, source: sourceLabel, timestamp: tsOpen, direction: 'unknown', type: 'data',
      contactNumber: '',  // don't show IPs as contacts
      durationSeconds: dur < 0 ? 0 : dur,
      dataMB: mb, ipAddress: ip, cellId: '', imei: '',
      raw: Object.fromEntries(hdrs.map((h, j) => [h, row[j] || ''])),
    };
  }).filter(r => r.timestamp);

  return { records, subNum };
}

// BDR ("Internet activity") files report NAT port-allocation sessions, not
// byte counts — a different schema from the TDR5 data-session files above
// (and 4G/5G BDR exports don't even share column names with each other:
// "Port Alloc/Dealloc Date/Time" + "MBL IP Addr(NAT)" on 4G vs "Mobile Port
// Chunk (de-)allocation Time" + "Mobile Node IP"/"IP Subscriber IP Address"
// on 5G). hIdx's substring matching lets one set of search terms cover both,
// as long as the alloc/dealloc terms keep their "port "/"chunk " prefix —
// dropping it would make 'alloc' match inside "de-alloc" too.
async function parseVzwBdr(file, sourceLabel) {
  const parsed = parseCSVText(await file.text());
  if (parsed.length < 2) return { records: [], subNum: '' };
  const hi = findHeaderRow(parsed);
  const hdrs = parsed[hi].map(h => h.trim());
  const rows = parsed.slice(hi + 1).filter(r => r.some(c => c));

  const msIdx       = hIdx(hdrs, 'msisdn');
  const allocIdx     = hIdx(hdrs, 'port alloc date/time', 'chunk allocation time');
  const deallocIdx    = hIdx(hdrs, 'port dealloc date/time', 'chunk de-allocation time');
  // The public/NAT'd address is explicitly labeled on 4G ("MBL IP Addr
  // NAT"); 5G has no such label, but "Mobile Node IP" carries public-
  // range values the same way, while "IP Subscriber IP Address" carries
  // private/CGNAT-range values like plain "MBL IP Addr" does on 4G.
  const natIpIdx      = hIdx(hdrs, 'mbl ip addr nat', 'mobile node ip');
  const mobileIpIdx    = hIdx(hdrs, 'ip subscriber ip address', 'mbl ip addr');
  const blockStartIdx  = hIdx(hdrs, 'block start');
  const blockEndIdx    = hIdx(hdrs, 'block end');

  const get = (row, idx) => idx >= 0 ? (row[idx] || '') : '';

  const freq = {};
  rows.forEach(r => { const n = normNum(get(r, msIdx)); if (n.length >= 10) freq[n] = (freq[n]||0)+1; });
  const subNum = Object.entries(freq).sort((a,b)=>b[1]-a[1])[0]?.[0] || '';

  const records = rows.map((row, idx) => {
    const tsOpen  = parseTsVzw(get(row, allocIdx));
    const tsClose = parseTsVzw(get(row, deallocIdx));
    const dur = tsOpen && tsClose ? Math.round((tsClose - tsOpen) / 1000) : 0;
    const ip  = get(row, natIpIdx) || get(row, mobileIpIdx);

    return {
      id: idx, source: sourceLabel, timestamp: tsOpen, direction: 'unknown', type: 'data',
      contactNumber: '',  // don't show IPs as contacts
      durationSeconds: dur < 0 ? 0 : dur,
      dataMB: 0, ipAddress: ip, cellId: '', imei: '',
      portBlockStart: get(row, blockStartIdx), portBlockEnd: get(row, blockEndIdx),
      raw: Object.fromEntries(hdrs.map((h, j) => [h, row[j] || ''])),
    };
  }).filter(r => r.timestamp);

  return { records, subNum };
}

// CDR-Billing: itemized voice-call billing records — Date/Time are separate
// columns (M/D/YY + h:mm AM/PM) rather than one combined timestamp, and
// there's no explicit direction column. Verizon's own convention here is
// that "Destination" reads a literal "Incoming ..." marker instead of a
// real place name when the subscriber received the call — a real place
// name means they placed it.
function parseVzwBillingTs(dateStr, timeStr) {
  const dm = String(dateStr || '').trim().match(/(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
  if (!dm) return null;
  const yr = dm[3].length === 2 ? 2000 + +dm[3] : +dm[3];
  const tm = String(timeStr || '').trim().match(/(\d{1,2}):(\d{2})\s*(AM|PM)?/i);
  let hour = 0, min = 0;
  if (tm) {
    hour = +tm[1]; min = +tm[2];
    if (tm[3]) hour = (hour % 12) + (/pm/i.test(tm[3]) ? 12 : 0);
  }
  return new Date(Date.UTC(yr, +dm[1] - 1, +dm[2], hour, min, 0));
}

async function parseVzwBilling(file) {
  const parsed = parseCSVText(await file.text());
  if (parsed.length < 2) return { records: [], subNum: '' };
  const hi = findHeaderRow(parsed);
  const hdrs = parsed[hi].map(h => h.trim());
  const rows = parsed.slice(hi + 1).filter(r => r.some(c => c));

  const mdnIdx      = hIdx(hdrs, 'detail for mdn');
  const dateIdx      = hIdx(hdrs, 'date');
  const timeIdx       = hIdx(hdrs, 'time');
  const numberIdx      = hIdx(hdrs, 'number');
  const destinationIdx  = hIdx(hdrs, 'destination');
  const minIdx           = hIdx(hdrs, 'min.', 'min');

  const get = (row, idx) => idx >= 0 ? (row[idx] || '') : '';

  const freq = {};
  rows.forEach(r => { const n = normNum(get(r, mdnIdx)); if (n.length >= 10) freq[n] = (freq[n]||0)+1; });
  const subNum = Object.entries(freq).sort((a,b)=>b[1]-a[1])[0]?.[0] || '';

  const records = rows.map((row, idx) => {
    const ts = parseVzwBillingTs(get(row, dateIdx), get(row, timeIdx));
    const destination = get(row, destinationIdx);
    const direction = /incoming/i.test(destination) ? 'incoming' : 'outgoing';
    const contact = normNum(get(row, numberIdx));
    const min = parseFloat(get(row, minIdx)) || 0;

    return {
      id: idx, source: 'CDR-Billing', timestamp: ts, direction, type: 'voice',
      contactNumber: contact,
      durationSeconds: Math.round(min * 60),
      cellId: '', imei: '',
      raw: Object.fromEntries(hdrs.map((h, j) => [h, row[j] || ''])),
    };
  }).filter(r => r.contactNumber || r.timestamp);

  return { records, subNum };
}

// Keyed by the number extracted from each file's own name (falling back to
// '' — "unknown" — when a filename doesn't match the expected pattern) so
// identity data for several different people's Verizon returns, uploaded
// together, doesn't get merged into one confused profile.
async function parseVzwSubscriberInfoByNumber(entries) {
  const blankInfo = () => ({
    name: '', firstName: '', lastName: '', address: '', city: '', state: '', zip: '',
    accountNumber: '', mtn: '', emails: [], imeis: [], devices: [], otherNumbers: [], ssn: '',
  });
  const byNum = {};
  const ensure = num => (byNum[num] || (byNum[num] = blankInfo()));

  for (const entry of entries) {
    const key = extractVzwFileNumber(entry.file.name);

    if (entry.kind === 'subscriber') {
      try {
        const parsed = parseCSVText(await entry.file.text());
        if (parsed.length < 2) continue;
        const hi = findHeaderRow(parsed);
        const hdrs = parsed[hi].map(h => h.toLowerCase().trim());
        const row  = parsed[hi + 1] || [];
        const g = (...names) => {
          for (const n of names) {
            const i = hdrs.findIndex(h => h.includes(n.toLowerCase()));
            if (i >= 0 && row[i]) return row[i].trim();
          }
          return '';
        };
        const info = ensure(key);
        info.firstName    = g('first name');
        info.lastName     = g('last name');
        info.name         = [info.firstName, info.lastName].filter(Boolean).join(' ');
        info.address      = g('addr line1');
        info.city         = g('city');
        info.state        = g('state');
        info.zip          = g('zip code', 'zip');
        info.accountNumber = g('account number');
        info.mtn          = g('mtn');
      } catch(e) {}
    }

    if (entry.kind === 'deviceid') {
      try {
        // Triple-quoted values: normalize before parsing
        const rawText = (await entry.file.text()).replace(/"""/g, '"');
        const parsed  = parseCSVText(rawText);
        if (parsed.length < 2) continue;
        const hi   = findHeaderRow(parsed);
        const hdrs = parsed[hi].map(h => h.toLowerCase().trim());
        const rows = parsed.slice(hi + 1).filter(r => r.some(c => c));
        const imeiIdx   = hIdx(hdrs, 'imei');
        const deviceIdx = hIdx(hdrs, 'eqp_name', 'device_type', 'mfg_name');
        const info = ensure(key);
        rows.forEach(row => {
          const imei   = (row[imeiIdx] || '').replace(/"+/g, '').trim();
          const device = (row[deviceIdx] || '').trim();
          if (imei) {
            info.imeis.push(imei);
            if (device) info.devices.push(device);
          }
        });
      } catch(e) {}
    }

    if (entry.kind === 'owneremail') {
      try {
        const parsed = parseCSVText(await entry.file.text());
        if (parsed.length < 2) continue;
        const hi   = findHeaderRow(parsed);
        const hdrs = parsed[hi].map(h => h.toLowerCase().trim());
        const rows = parsed.slice(hi + 1).filter(r => r.some(c => c));
        const emailIdx = hIdx(hdrs, 'email');
        const info = ensure(key);
        rows.forEach(row => {
          const e = (row[emailIdx] || '').trim();
          if (e && !info.emails.includes(e)) info.emails.push(e);
        });
      } catch(e) {}
    }

    if (entry.kind === 'othernumbers') {
      try {
        const parsed = parseCSVText(await entry.file.text());
        if (parsed.length < 2) continue;
        const hi   = findHeaderRow(parsed);
        const hdrs = parsed[hi].map(h => h.toLowerCase().trim());
        const rows = parsed.slice(hi + 1).filter(r => r.some(c => c));
        const numIdx = hIdx(hdrs, 'other mtn');
        const info = ensure(key);
        rows.forEach(row => {
          const n = normNum(row[numIdx] || '');
          if (n.length >= 10 && !info.otherNumbers.includes(n)) info.otherNumbers.push(n);
        });
      } catch(e) {}
    }

    if (entry.kind === 'ssn') {
      try {
        const parsed = parseCSVText(await entry.file.text());
        if (parsed.length < 2) continue;
        const hi   = findHeaderRow(parsed);
        const hdrs = parsed[hi].map(h => h.toLowerCase().trim());
        const row  = parsed[hi + 1] || [];
        const i = hIdx(hdrs, 'ssn last-4', 'ssn last4');
        if (i >= 0) ensure(key).ssn = (row[i] || '').trim();
      } catch(e) {}
    }
  }

  return byNum;
}

// Each Verizon file is queried against one subject number, encoded in its
// own filename (see extractVzwFileNumber) — that's the authoritative source
// for attribution, with the in-file MSISDN frequency only as a fallback for
// filenames that don't match the expected convention. Records AND
// subscriber-info are split per number so several people's Verizon returns
// can be uploaded together without collapsing into one response.
async function processVerizonFiles(entries) {
  const allRecords = [];
  const fileNamesByNumber = {};
  const addFileName = (num, name) => (fileNamesByNumber[num] || (fileNamesByNumber[num] = [])).push(name);

  for (const entry of entries) {
    const fileNum = extractVzwFileNumber(entry.file.name);
    if (entry.skip) { addFileName(fileNum, entry.file.name); continue; }
    try {
      let result;
      if (entry.kind === 'volte4g' || entry.kind === 'voltevisible') {
        result = await parseVzwVoLTE4G(entry.file);
      } else if (entry.kind === 'voice5g') {
        result = await parseVzwVoice5G(entry.file);
      } else if (entry.kind === 'billing') {
        result = await parseVzwBilling(entry.file);
      } else if (entry.kind === 'data5g') {
        result = await parseVzwData(entry.file, 'TDR5-5G');
      } else if (entry.kind === 'bdr4g') {
        result = await parseVzwBdr(entry.file, 'BDR-4G');
      } else if (entry.kind === 'bdr5g') {
        result = await parseVzwBdr(entry.file, 'BDR-5G');
      }
      if (result) {
        const sub = fileNum || result.subNum || '';
        result.records.forEach(rec => { rec.sourceFile = entry.file.name; rec.subNum = sub; });
        allRecords.push(...result.records);
        addFileName(sub, entry.file.name);
      } else {
        addFileName(fileNum, entry.file.name);
      }
    } catch(e) {
      console.error(`Error parsing ${entry.file.name}:`, e);
      addFileName(fileNum, entry.file.name);
    }
  }

  allRecords.forEach((r, i) => { r.id = i; });

  const infoByNumber = await parseVzwSubscriberInfoByNumber(entries);

  const tsList = allRecords.map(r => r.timestamp).filter(Boolean);
  return {
    carrier: 'verizon', records: allRecords, infoByNumber, fileNamesByNumber,
    minDate: tsList.length ? new Date(Math.min(...tsList.map(t => t.getTime()))) : null,
    maxDate: tsList.length ? new Date(Math.max(...tsList.map(t => t.getTime()))) : null,
  };
}

// ── AT&T legal-demand response parsing ─────────────────────────────────
//
// AT&T returns are plain fixed-width/CSV-hybrid .txt reports, not a single
// tabular export. Two families:
//   A) fixed-width "report card" files: ReportCT (subscriber), ReportCAPM
//      (payments), ReportIMEI (device/MAC), ReportAccountNotesSF (notes)
//   B) header-block + CSV files: ReportAU (mobility CDR), ReportICDR
//      (international CDR), ReportLandline (wireline CDR), ReportStirShaken
//      (call attestation) — each can contain multiple "Voice Usage For:"
//      sections (one per queried number), each with its own CSV column row.
// PDFs that accompany a return carry no parseable data and are ignored.

// MM/DD/YY[YY][ HH:MM[:SS] [AM/PM]] or YYYY-MM-DD HH:MM:SS. AT&T's CDR
// timestamps are already UTC (the header's "Eastern Time Zone" line refers
// only to the search window, not the data), matching this tool's default
// source zone.
function parseAttTs(raw) {
  if (!raw) return null;
  const s = String(raw).trim();
  const m = s.match(/(\d{1,2})\/(\d{1,2})\/(\d{2,4})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)?)?/i);
  if (m) {
    const [, mo, da, yr, hh, mi, ss, ap] = m;
    const year = yr.length === 2 ? 2000 + +yr : +yr;
    let hour = hh ? +hh : 0;
    if (ap) hour = (hour % 12) + (/pm/i.test(ap) ? 12 : 0);
    return new Date(Date.UTC(year, +mo - 1, +da, hour, +(mi || 0), +(ss || 0)));
  }
  const iso = s.match(/(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2}):(\d{2})/);
  if (iso) return new Date(Date.UTC(+iso[1], +iso[2] - 1, +iso[3], +iso[4], +iso[5], +iso[6]));
  return null;
}

// "0:27" / "3:12" (M+:SS), "1:02:33" (H:MM:SS), or a bare integer already
// in seconds → seconds.
function parseMMSS(raw) {
  const s = String(raw || '').trim();
  const parts = s.split(':');
  if (parts.length === 2 && /^\d+$/.test(parts[0]) && /^\d{1,2}$/.test(parts[1])) {
    return (+parts[0]) * 60 + (+parts[1]);
  }
  if (parts.length === 3 && parts.every(p => /^\d+$/.test(p))) {
    return (+parts[0]) * 3600 + (+parts[1]) * 60 + (+parts[2]);
  }
  if (/^\d+$/.test(s)) return +s;
  return 0;
}

// Extracts "Label: value" pairs from fixed-width label lines. Handles two
// labeled fields packed onto one physical line (e.g. the CT report's
// "Photo ID Type:" / "Photo ID State:" row) by splitting on runs of 2+
// spaces before matching each chunk.
function parseLabelValuePairs(text) {
  const out = {};
  for (const line of text.split(/\r?\n/)) {
    if (!line.includes(':')) continue;
    // Re-merge a chunk into the previous one unless it itself starts a new
    // "Label:" — otherwise a wide fixed-width gap between a colon and its
    // own value (e.g. "Target Phone Number:        (917) 532-1078") gets
    // mistaken for two side-by-side fields and the value is dropped.
    const rawChunks = line.split(/ {2,}/).map(c => c.trim()).filter(Boolean);
    const chunks = [];
    for (const c of rawChunks) {
      if (chunks.length === 0 || /^[A-Za-z][A-Za-z0-9 /]*:/.test(c)) chunks.push(c);
      else chunks[chunks.length - 1] += ' ' + c;
    }
    for (const chunk of chunks) {
      const m = chunk.match(/^([A-Za-z][A-Za-z0-9 /]*):\s*(.*)$/);
      if (m && m[1].trim()) out[m[1].trim()] = m[2].trim();
    }
  }
  return out;
}

function splitAttAddress(raw) {
  const addr = raw || '';
  const m = addr.match(/^(.*),\s*([^,]+),\s*([A-Z]{2})\s*(\d{5}(?:-\d{4})?)$/);
  if (m) return { street: m[1].trim(), city: m[2].trim(), state: m[3].trim(), zip: m[4].trim() };
  return { street: addr, city: '', state: '', zip: '' };
}

function extractAttMatterId(text) {
  const m = text.match(/Matter ID:\s*(\S+)/i);
  if (m) return m[1];
  const firstLine = text.split(/\r?\n/).find(l => l.trim());
  return firstLine && /^\d{5,}$/.test(firstLine.trim()) ? firstLine.trim() : '';
}

// ReportCT: the file concatenates one "WIRELESS SUBSCRIBER INFORMATION"
// block per queried MSISDN, interleaved with FEATURES and an abbreviated
// ACCOUNT NOTES block per number (same repeating-disclaimer-footer
// structure as ReportIMEI). Only the SUBSCRIBER INFORMATION blocks are
// parsed here — FEATURES isn't requested, and the standalone
// ReportAccountNotesSF file already covers notes in more detail.
// Each subscriber block itself has three sub-sections (FINANCIAL LIABLE
// PARTY / BILLING PARTY / USER INFORMATION) that repeat some of the same
// label names (e.g. "Name") with different values, so they're parsed
// independently rather than as one flat key/value pass.
function parseAttCTBlock(block) {
  const sectionNames = ['FINANCIAL LIABLE PARTY', 'BILLING PARTY', 'USER INFORMATION', 'STATUS CHANGE HISTORY'];
  const sections = { header: [] };
  let cur = 'header';
  for (const line of block.split(/\r?\n/)) {
    const trimmed = line.trim();
    const match = sectionNames.find(n => trimmed === n);
    if (match) { cur = match; sections[cur] = []; continue; }
    sections[cur].push(line);
  }
  const kv = name => parseLabelValuePairs((sections[name] || []).join('\n'));
  const flp = kv('FINANCIAL LIABLE PARTY');
  const bp = kv('BILLING PARTY');
  const ui = kv('USER INFORMATION');

  const financialLiableParty = {
    name: flp['Name'] || '', ...splitAttAddress(flp['Credit Address'] || ''),
    customerSince: flp['Customer Since'] || '', dob: flp['DOB'] || '', ssn: flp['SSN'] || '',
    photoIdType: flp['Photo ID Type'] || '', photoIdState: flp['Photo ID State'] || '',
    contactHomePhone: flp['Contact Home Phone'] || '', contactWorkPhone: flp['Contact Work Phone'] || '',
    contactHomeEmail: flp['Contact Home Email'] || '', contactWorkEmail: flp['Contact Work Email'] || '',
  };
  const billingParty = {
    accountNumber: bp['Account Number'] || '', name: bp['Name'] || '', ...splitAttAddress(bp['Billing Address'] || ''),
    status: bp['Account Status'] || '', billingCycle: bp['Billing Cycle'] || '',
  };
  const userInfo = {
    msisdn: normNum(ui['MSISDN'] || ''), imsi: ui['IMSI'] || '',
    msisdnActive: ui['MSISDN Active'] || '', name: ui['Name'] || '', ...splitAttAddress(ui['User Address'] || ''),
    serviceStartDate: ui['Service Start Date'] || '', dealerInfo: ui['Dealer Info'] || '',
    paymentType: ui['Payment Type'] || '',
  };

  return { msisdn: userInfo.msisdn, financialLiableParty, billingParty, userInfo };
}

function parseAttCT(text) {
  const subscribers = [];
  for (const block of text.split(/AT&T PROPRIETARY[\s\S]*?general distribution\.?/i)) {
    if (!block.trim() || !/SUBSCRIBER INFORMATION/i.test(block)) continue;
    subscribers.push(parseAttCTBlock(block));
  }
  return subscribers;
}

// ReportCAPM: like ReportIMEI/ReportCT, one Target Phone Number block per
// queried number, separated by the repeating disclaimer footer. Each
// block's transaction table is fixed-width, not comma-delimited, and
// "Billing Acct Num" / "Payment Detail" abut with a single space, so the
// trailing chunk needs a second split.
function parseAttCAPM(text) {
  const results = [];
  for (const block of text.split(/AT&T PROPRIETARY[\s\S]*?general distribution\.?/i)) {
    if (!block.trim() || !/PAYMENT INFORMATION/i.test(block)) continue;
    const kv = parseLabelValuePairs(block);
    const targetNumber = normNum(kv['Target Phone Number'] || '');
    const payments = [];
    for (const line of block.split(/\r?\n/)) {
      if (!/^\s*\d{1,2}\/\d{1,2}\/\d{4}\s/.test(line)) continue;
      const chunks = line.trim().split(/ {2,}/).map(c => c.trim());
      const [postedDate, amount, type, method, name, source, rest] = chunks;
      const restMatch = (rest || '').match(/^(\d+)\s*(.*)$/);
      payments.push({
        number: targetNumber, postedDate, amount, type, method, name, source,
        billingAcct: restMatch ? restMatch[1] : '',
        detail: restMatch ? restMatch[2].trim() : (rest || ''),
      });
    }
    results.push({ targetNumber, billingAcct: kv['Search By Billing Acct Num'] || '', payments });
  }
  return results;
}

// ReportIMEI: repeated concatenated blocks (one per phone number/as-of
// timestamp), each terminated by a repeating AT&T proprietary disclaimer
// footer that must be treated as noise, not a real section boundary.
function parseAttImei(text) {
  const devices = [];
  for (const block of text.split(/AT&T PROPRIETARY[\s\S]*?general distribution\.?/i)) {
    if (!block.trim()) continue;
    const isMac = /MAC Address Report/i.test(block);
    const isImei = /IMEI Report/i.test(block);
    if (!isMac && !isImei) continue;
    const numM = block.match(/report for:\s*(.+)/i);
    const asOfM = block.match(/As Of:\s*(.+)/i);
    const valM = block.match(/Associated (?:IMEI|MAC Address):\s*(.*)/i);
    const value = valM ? valM[1].trim().split(/ {2,}/)[0] : '';
    if (!value || /not found/i.test(value)) continue;
    devices.push({
      number: numM ? normNum(numM[1]) : '',
      asOf: asOfM ? asOfM[1].trim() : '',
      kind: isMac ? 'mac' : 'imei',
      value,
    });
  }
  return devices;
}

// ReportAccountNotesSF: can contain notes for multiple numbers in one file,
// each block introduced by "Searched by: MDN <number>". Within a block,
// each "row" wraps across several ragged, indented physical lines — anchor
// on the MM/DD/YYYY HH:MM:SS AM/PM timestamp that starts a record, then
// fold every following line up to the next timestamp (or the footer) into
// that record's free-text note.
function parseAttAccountNotes(text) {
  const tsRe = /^(\d{1,2}\/\d{1,2}\/\d{4}\s+\d{1,2}:\d{2}:\d{2}\s*[AP]M)\s*(.*)$/i;
  const results = [];
  let currentMdn = '';
  let cur = null;
  const flush = () => { if (cur) { results.push(cur); cur = null; } };
  for (const line of text.split(/\r?\n/)) {
    const mdnMatch = line.match(/Searched by:\s*MDN\s*(\d+)/i);
    if (mdnMatch) { flush(); currentMdn = normNum(mdnMatch[1]); continue; }
    const m = line.match(tsRe);
    if (m) { flush(); cur = { number: currentMdn, timestamp: parseAttTs(m[1]), text: m[2].trim() }; continue; }
    if (!cur) continue;
    if (/AT&T PROPRIETARY/i.test(line) || /^-{5,}/.test(line)) { flush(); continue; }
    const t = line.trim();
    if (t) cur.text += (cur.text ? ' ' : '') + t;
  }
  flush();
  return results;
}

// Family B (AU/ICDR/Landline/StirShaken): splits a file into its
// "Voice/Data/SMS/SMS-Email Usage For:" sections, each re-detecting its own
// CSV column row — a file covering multiple queried numbers (or, for
// ReportAU, multiple usage types per number) has one header per section.
// Row capture is gated on the line actually looking like "<n>,..." (the
// Item column) — without that, the repeating disclaimer footer and the
// next block's header/preamble lines (which fall between one section's
// last real row and the next "Usage For:" line) get swallowed as bogus
// rows.
function splitAttSections(text) {
  const sections = [];
  let cur = null;
  for (const line of text.split(/\r?\n/)) {
    const su = line.match(/(Voice Usage For|Data Usage For|SMS-Email Usage For|SMS Usage For):\s*(.+)/i);
    if (su) {
      const label = su[1].toLowerCase();
      const kind = label.includes('sms-email') ? 'sms-email' : label.includes('sms') ? 'sms' : label.includes('data') ? 'data' : 'voice';
      cur = { kind, subscriberNumber: normNum(su[2]), headers: null, rows: [] };
      sections.push(cur);
      continue;
    }
    if (!cur) continue;
    if (/^\s*Item\s*,/i.test(line)) { cur.headers = parseCSVText(line)[0].map(h => h.trim()); continue; }
    if (cur.headers && line.trim() && /^\s*\d+\s*,/.test(line)) {
      const row = parseCSVText(line)[0];
      if (row && row.length) cur.rows.push(row);
    }
  }
  return sections;
}

function colIdx(headers, name) {
  const nl = name.toLowerCase();
  return headers.findIndex(h => h.trim().toLowerCase() === nl);
}

// AT&T's duration column is named "ET" in some report exports and
// "ElapsedTime" in others (both mean the same "Elapsed Time" field per
// AT&T's own records key) — try every known spelling first, then fall back
// to a looser match ("ET" as its own leading token, or any header
// mentioning "elapsed"/"duration") to catch variants like "ET (MM:SS)"
// without accidentally matching an unrelated column like "SeizureTime"
// (which contains the substring "et" but not as its own word).
function colIdxDuration(headers) {
  for (const name of ['ET', 'ElapsedTime', 'Elapsed Time', 'Duration']) {
    const i = colIdx(headers, name);
    if (i >= 0) return i;
  }
  return headers.findIndex(h => {
    const hl = h.trim().toLowerCase();
    return /^et\b/.test(hl) || hl.includes('elapsed') || hl.includes('duration');
  });
}

function mostCommonAttSub(sections) {
  const freq = {};
  sections.forEach(s => { if (s.subscriberNumber) freq[s.subscriberNumber] = (freq[s.subscriberNumber] || 0) + (s.rows.length || 1); });
  return Object.entries(freq).sort((a, b) => b[1] - a[1])[0]?.[0] || '';
}

function attDirection(orig, term, sub) {
  if (sub && orig === sub) return { direction: 'outgoing', contact: term };
  if (sub && term === sub) return { direction: 'incoming', contact: orig };
  return { direction: 'unknown', contact: term || orig };
}

// ReportAU (Mobility CDR): one file, up to four usage types per queried
// number (Voice/Data/SMS/SMS-Email), each with its own column layout.
//
// Voice: each real call is emitted as two adjacent rows (CT=MO / CT=MT)
// sharing timestamp + numbers — the MO row is the leg on AT&T's own
// network and carries the subscriber's IMEI/make/model, the MT row is a
// bare mirror with no device info. Dedupe to one record per call,
// preferring the MO row for its richer data.
//
// SMS: the same MO/MT-style pairing happens via Desc=SMSO/SMST, though not
// every message has both legs captured. Direction is still derived by
// comparing Originating/Terminating against the subscriber number (not
// the SMSO/SMST label, which marks network leg, not who sent it) — dedupe
// on (timestamp, orig, term), preferring whichever row has IMEI/IMSI.
//
// SMS-Email: gateway messages have no TerminatingNumber (the destination
// is an email address, not a phone number), so direction comes from the
// Desc field instead: MO/AO (mobile/application originating) = outgoing,
// MT/AT (…terminating) = incoming. The email address is stored as the
// contact.
//
// Data: no "contact" (a data session isn't a call/message to anyone) —
// direction is 'unknown' and dataMB is computed from BytesUp+BytesDn.
function parseAttAU(text) {
  const sections = splitAttSections(text);
  const records = [];
  sections.forEach(sec => {
    if (!sec.headers) return;
    const sub = sec.subscriberNumber;

    if (sec.kind === 'voice') {
      const i = {
        ts: colIdx(sec.headers, 'ConnDateTime(UTC)'), et: colIdxDuration(sec.headers),
        orig: colIdx(sec.headers, 'OriginatingNumber'), term: colIdx(sec.headers, 'TerminatingNumber'),
        imei: colIdx(sec.headers, 'IMEI'), ct: colIdx(sec.headers, 'CT'),
      };
      const byKey = new Map();
      sec.rows.forEach(row => {
        const ts = parseAttTs(row[i.ts]);
        const orig = normNum(row[i.orig]), term = normNum(row[i.term]);
        const ct = (row[i.ct] || '').toUpperCase().trim();
        const key = `${ts ? ts.getTime() : ''}|${orig}|${term}`;
        const existing = byKey.get(key);
        if (!existing || (ct === 'MO' && existing.ct !== 'MO')) {
          byKey.set(key, { ts, orig, term, ct, imei: row[i.imei] || '', et: row[i.et] || '', row });
        }
      });
      byKey.forEach(r => {
        const { direction, contact } = attDirection(r.orig, r.term, sub);
        records.push({
          source: 'ReportAU (Voice)', timestamp: r.ts, direction, type: 'voice', subNum: sub,
          contactNumber: contact || '', durationSeconds: parseMMSS(r.et),
          cellId: '', imei: r.imei,
          raw: Object.fromEntries(sec.headers.map((h, j) => [h, r.row[j] || ''])),
        });
      });
    } else if (sec.kind === 'data') {
      const i = {
        ts: colIdx(sec.headers, 'ConnDateTime(UTC)'), et: colIdxDuration(sec.headers),
        up: colIdx(sec.headers, 'BytesUp'), dn: colIdx(sec.headers, 'BytesDn'),
        imei: colIdx(sec.headers, 'IMEI'),
      };
      sec.rows.forEach(row => {
        const up = parseFloat(row[i.up]) || 0, dn = parseFloat(row[i.dn]) || 0;
        records.push({
          source: 'ReportAU (Data)', timestamp: parseAttTs(row[i.ts]), direction: 'unknown', type: 'data', subNum: sub,
          contactNumber: '', durationSeconds: parseMMSS(row[i.et]), dataMB: (up + dn) / (1024 * 1024),
          cellId: '', imei: row[i.imei] || '',
          raw: Object.fromEntries(sec.headers.map((h, j) => [h, row[j] || ''])),
        });
      });
    } else if (sec.kind === 'sms') {
      const i = {
        ts: colIdx(sec.headers, 'ConnDateTime(UTC)'),
        orig: colIdx(sec.headers, 'OriginatingNumber'), term: colIdx(sec.headers, 'TerminatingNumber'),
        mms: colIdx(sec.headers, 'MMSType'), imei: colIdx(sec.headers, 'IMEI'),
      };
      const byKey = new Map();
      sec.rows.forEach(row => {
        const ts = parseAttTs(row[i.ts]);
        const orig = normNum(row[i.orig]), term = normNum(row[i.term]);
        const key = `${ts ? ts.getTime() : ''}|${orig}|${term}`;
        const hasImei = !!(row[i.imei] || '').trim();
        const existing = byKey.get(key);
        if (!existing || (hasImei && !existing.hasImei)) {
          byKey.set(key, { ts, orig, term, mms: row[i.mms] || '', imei: row[i.imei] || '', hasImei, row });
        }
      });
      byKey.forEach(r => {
        const { direction, contact } = attDirection(r.orig, r.term, sub);
        records.push({
          source: 'ReportAU (SMS)', timestamp: r.ts, direction, type: r.mms.trim() ? 'mms' : 'sms', subNum: sub,
          contactNumber: contact || '', durationSeconds: 0,
          cellId: '', imei: r.imei,
          raw: Object.fromEntries(sec.headers.map((h, j) => [h, r.row[j] || ''])),
        });
      });
    } else if (sec.kind === 'sms-email') {
      const i = {
        ts: colIdx(sec.headers, 'ConnDateTime(UTC)'),
        email: colIdx(sec.headers, 'Email'), desc: colIdx(sec.headers, 'Desc'),
      };
      sec.rows.forEach(row => {
        const desc = (row[i.desc] || '').toUpperCase().trim();
        const direction = /^(MO|AO)$/.test(desc) ? 'outgoing' : /^(MT|AT)$/.test(desc) ? 'incoming' : 'unknown';
        records.push({
          source: 'ReportAU (SMS-Email)', timestamp: parseAttTs(row[i.ts]), direction, type: 'sms', subNum: sub,
          contactNumber: (row[i.email] || '').trim(), durationSeconds: 0,
          cellId: '', imei: '',
          raw: Object.fromEntries(sec.headers.map((h, j) => [h, row[j] || ''])),
        });
      });
    }
  });
  return { records, subNum: mostCommonAttSub(sections) };
}

function parseAttICDR(text) {
  const sections = splitAttSections(text);
  const records = [];
  sections.forEach(sec => {
    if (!sec.headers) return;
    const iTs = colIdx(sec.headers, 'ConnDateTime(UTC)'), iOrig = colIdx(sec.headers, 'OriginatingNumber');
    const iDialed = colIdx(sec.headers, 'DialedNumber'), iElapsed = colIdxDuration(sec.headers);
    const sub = sec.subscriberNumber;
    sec.rows.forEach(row => {
      const orig = normNum(row[iOrig]), term = normNum(row[iDialed]);
      const { direction, contact } = attDirection(orig, term, sub);
      records.push({
        source: 'ReportICDR', timestamp: parseAttTs(row[iTs]), direction, type: 'voice', subNum: sub,
        contactNumber: contact || '', durationSeconds: parseMMSS(row[iElapsed]),
        cellId: '', imei: '',
        raw: Object.fromEntries(sec.headers.map((h, j) => [h, row[j] || ''])),
      });
    });
  });
  return { records, subNum: mostCommonAttSub(sections) };
}

function parseAttLandline(text) {
  const sections = splitAttSections(text);
  const records = [];
  sections.forEach(sec => {
    if (!sec.headers) return;
    const iTs = colIdx(sec.headers, 'ConnDateTime(UTC)'), iOrig = colIdx(sec.headers, 'OriginatingNumber');
    const iTerm = colIdx(sec.headers, 'TerminatingNumber'), iDialed = colIdx(sec.headers, 'DialedNumber');
    const iElapsed = colIdxDuration(sec.headers);
    const sub = sec.subscriberNumber;
    sec.rows.forEach(row => {
      const orig = normNum(row[iOrig]), term = normNum(row[iTerm]) || normNum(row[iDialed]);
      const { direction, contact } = attDirection(orig, term, sub);
      records.push({
        source: 'ReportLandline', timestamp: parseAttTs(row[iTs]), direction, type: 'voice', subNum: sub,
        contactNumber: contact || '', durationSeconds: parseMMSS(row[iElapsed]),
        cellId: '', imei: '',
        raw: Object.fromEntries(sec.headers.map((h, j) => [h, row[j] || ''])),
      });
    });
  });
  return { records, subNum: mostCommonAttSub(sections) };
}

function parseAttStirShaken(text) {
  const sections = splitAttSections(text);
  const records = [];
  sections.forEach(sec => {
    if (!sec.headers) return;
    const iTs = colIdx(sec.headers, 'ConnDateTime(UTC)'), iOrig = colIdx(sec.headers, 'OriginatingNumber');
    const iTerm = colIdx(sec.headers, 'TerminatingNumber'), iStatus = colIdx(sec.headers, 'Status');
    const iAttest = colIdx(sec.headers, 'AttestType');
    const sub = sec.subscriberNumber;
    sec.rows.forEach(row => {
      const orig = normNum(row[iOrig]), term = normNum(row[iTerm]);
      const { direction, contact } = attDirection(orig, term, sub);
      records.push({
        source: 'ReportStirShaken', timestamp: parseAttTs(row[iTs]), direction, type: 'voice', subNum: sub,
        contactNumber: contact || '', durationSeconds: 0, cellId: '', imei: '',
        attestStatus: row[iStatus] || '', attestType: row[iAttest] || '',
        raw: Object.fromEntries(sec.headers.map((h, j) => [h, row[j] || ''])),
      });
    });
  });
  return { records, subNum: mostCommonAttSub(sections) };
}

// Classifies one AT&T .txt report by filename first (AT&T's own naming is
// consistent — ReportCT_, ReportAU_, etc.), falling back to the title text
// each report family always carries, in case a file gets renamed.
function classifyAttReport(fileName, text) {
  const name = fileName.toLowerCase();
  const head = text.slice(0, 4000);
  if (/reportct[_.]/i.test(name) || /subscriber information/i.test(head)) return 'ct';
  if (/reportcapm/i.test(name) || /payment information/i.test(head)) return 'capm';
  if (/reportimei/i.test(name) || /imei report|mac address report/i.test(head)) return 'imei';
  if (/reportaccountnotes/i.test(name) || /account notes/i.test(head)) return 'notes';
  if (/reportau[_.]/i.test(name) || /^\s*mobility\s*$/im.test(head)) return 'au';
  if (/reporticdr/i.test(name) || /^\s*international\s*$/im.test(head)) return 'icdr';
  if (/reportlandline/i.test(name) || /^\s*wireline\s*$/im.test(head)) return 'landline';
  if (/reportstirshaken/i.test(name) || /stir\s*\/\s*shaken/i.test(head)) return 'stirshaken';
  return 'unknown';
}

function buildAttSubInfo() {
  return {
    name: '', address: '', city: '', state: '', zip: '', dob: '', ssn: '',
    customerSince: '', photoIdType: '', photoIdState: '', accountNumber: '',
    matterId: '', mtn: '', imeis: [], devices: [], emails: [], otherNumbers: [],
    payments: [], notes: [], billingParty: null, userInfo: null,
  };
}

// Merges every AT&T .txt file in one legal-demand response into a merged
// record set plus a subscriber profile keyed by queried number —
// ReportCT/ReportCAPM/ReportAccountNotesSF/ReportIMEI can each cover
// several numbers in one file, so subscriber data is kept per-number
// rather than flattened into one blob. PDFs are skipped entirely — AT&T's
// own case exports include PDF cover sheets/certifications with no record
// data.
async function processAttFiles(files) {
  const allRecords = [];
  const infoByNumber = {};
  const ensureInfo = num => {
    const key = num || '';
    if (!infoByNumber[key]) infoByNumber[key] = buildAttSubInfo();
    return infoByNumber[key];
  };
  let matterId = '';
  let primaryNum = '';
  const fileNames = [];

  for (const file of files) {
    if (/\.pdf$/i.test(file.name)) continue;
    fileNames.push(file.name);
    try {
      const text = await file.text();
      if (!matterId) { const mid = extractAttMatterId(text); if (mid) matterId = mid; }
      const kind = classifyAttReport(file.name, text);

      if (kind === 'ct') {
        parseAttCT(text).forEach(s => {
          const info = ensureInfo(s.msisdn);
          info.name = s.financialLiableParty.name || s.userInfo.name || info.name;
          info.dob = s.financialLiableParty.dob || info.dob;
          info.ssn = s.financialLiableParty.ssn || info.ssn;
          info.customerSince = s.financialLiableParty.customerSince || info.customerSince;
          info.photoIdType = s.financialLiableParty.photoIdType || info.photoIdType;
          info.photoIdState = s.financialLiableParty.photoIdState || info.photoIdState;
          info.accountNumber = s.billingParty.accountNumber || info.accountNumber;
          info.address = s.financialLiableParty.street || info.address;
          info.city = s.financialLiableParty.city || info.city;
          info.state = s.financialLiableParty.state || info.state;
          info.zip = s.financialLiableParty.zip || info.zip;
          info.billingParty = s.billingParty;
          info.userInfo = s.userInfo;
          if (!primaryNum) primaryNum = s.msisdn;
        });
      } else if (kind === 'capm') {
        parseAttCAPM(text).forEach(b => {
          const info = ensureInfo(b.targetNumber);
          info.payments.push(...b.payments);
          if (b.billingAcct && !info.accountNumber) info.accountNumber = b.billingAcct;
          if (!primaryNum && b.targetNumber) primaryNum = b.targetNumber;
        });
      } else if (kind === 'imei') {
        parseAttImei(text).forEach(d => {
          if (d.kind !== 'imei') return;
          const info = ensureInfo(d.number);
          info.imeis.push(d.value);
          info.devices.push(d.asOf ? `as of ${d.asOf}` : '');
          if (!primaryNum && d.number) primaryNum = d.number;
        });
      } else if (kind === 'notes') {
        parseAttAccountNotes(text).forEach(n => {
          const info = ensureInfo(n.number);
          info.notes.push(n);
          if (!primaryNum && n.number) primaryNum = n.number;
        });
      } else if (kind === 'au') {
        const r = parseAttAU(text); r.records.forEach(rec => { rec.sourceFile = file.name; });
        allRecords.push(...r.records); if (!primaryNum) primaryNum = r.subNum;
      } else if (kind === 'icdr') {
        const r = parseAttICDR(text); r.records.forEach(rec => { rec.sourceFile = file.name; });
        allRecords.push(...r.records); if (!primaryNum) primaryNum = r.subNum;
      } else if (kind === 'landline') {
        const r = parseAttLandline(text); r.records.forEach(rec => { rec.sourceFile = file.name; });
        allRecords.push(...r.records); if (!primaryNum) primaryNum = r.subNum;
      } else if (kind === 'stirshaken') {
        const r = parseAttStirShaken(text); r.records.forEach(rec => { rec.sourceFile = file.name; });
        allRecords.push(...r.records); if (!primaryNum) primaryNum = r.subNum;
      }
    } catch (e) {
      console.error(`Error parsing AT&T file ${file.name}:`, e);
    }
  }

  Object.values(infoByNumber).forEach(info => { info.matterId = matterId; info.mtn = primaryNum; });
  allRecords.forEach((r, i) => { r.id = i; });
  const tsList = allRecords.map(r => r.timestamp).filter(Boolean);
  return {
    carrier: 'att', subNum: primaryNum, infoByNumber, records: allRecords, headers: [],
    fileNames, fileName: `${fileNames.length} AT&T file${fileNames.length !== 1 ? 's' : ''}`,
    minDate: tsList.length ? new Date(Math.min(...tsList.map(t => t.getTime()))) : null,
    maxDate: tsList.length ? new Date(Math.max(...tsList.map(t => t.getTime()))) : null,
  };
}

// ── Carrier column name map (T-Mobile) ─────────────────────────────────

const CARRIER_FIELDS = {
  tmobile: {
    datetime:  ['date/time', 'start date/time', 'datetime', 'timestamp', 'date time', 'start date time'],
    date:      ['date', 'start date', 'call date', 'event date'],
    time:      ['time', 'start time', 'call time', 'event time'],
    from:      ['from', 'from number', 'originating number', 'subscriber number', 'subscriber num', 'msisdn', 'mdn', 'a number', 'calling number', 'source number'],
    to:        ['to', 'to number', 'terminating number', 'destination number', 'dialed number', 'connected number', 'b number', 'called number'],
    type:      ['type', 'call type', 'record type', 'service type', 'event type', 'usage type', 'feature'],
    duration:  ['duration', 'duration (sec)', 'duration (seconds)', 'elapsed', 'seconds', 'call duration'],
    cell:      ['cell id', 'cell site', 'site id', 'tower id', 'cell tower', 'cell', 'bts', 'enodeb', 'sector', 'location', 'serving cell'],
    imei:      ['imei', 'device id', 'handset imei', 'equipment id'],
  },
};

function detectFields(headers, carrier) {
  const fieldDefs = CARRIER_FIELDS[carrier];
  if (!fieldDefs) return {};
  const map = {};
  const hls = headers.map(h => String(h || '').toLowerCase().trim());

  // Pass 1: exact header==variant matches, in field-priority order — so a
  // bare "Date" column claims the 'date' field before a longer variant like
  // "Date/Time" can steal it via substring containment (e.g. "date/time"
  // contains "date").
  for (const [field, variants] of Object.entries(fieldDefs)) {
    if (field in map) continue;
    const i = hls.findIndex((hl, idx) => hl && variants.includes(hl) && !Object.values(map).includes(idx));
    if (i >= 0) map[field] = i;
  }

  // Pass 2: substring containment fallback for anything still unmapped.
  hls.forEach((hl, i) => {
    if (!hl || Object.values(map).includes(i)) return;
    for (const [field, variants] of Object.entries(fieldDefs)) {
      if (!(field in map) && variants.some(v => hl.includes(v) || v.includes(hl))) {
        map[field] = i;
        return;
      }
    }
  });

  return map;
}

// ── Normalizers ───────────────────────────────────────────────────────

function normNum(raw) {
  const d = String(raw || '').replace(/\D/g, '');
  if (d.length === 11 && d[0] === '1') return d.slice(1);
  return d;
}

export function fmtNum(num) {
  const n = normNum(num);
  if (n.length === 10) return `(${n.slice(0, 3)}) ${n.slice(3, 6)}-${n.slice(6)}`;
  return String(num || '');
}

function parseTs(dv, tv) {
  if (!dv) return null;
  const s = tv ? `${String(dv).trim()} ${String(tv).trim()}` : String(dv).trim();
  // Explicit MM/DD/YYYY[ HH:MM[:SS]] — parsed as a naive wall-clock (UTC fields)
  // so timezone conversion is applied later, consistently for every carrier.
  const m = s.match(/(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})(?:[ T]+(\d{1,2}):(\d{2})(?::(\d{2}))?)?/);
  if (m) {
    const yr = m[3].length === 2 ? 2000 + +m[3] : +m[3];
    return new Date(Date.UTC(yr, +m[1] - 1, +m[2], +(m[4] || 0), +(m[5] || 0), +(m[6] || 0)));
  }
  const d = new Date(s);
  if (isNaN(d.getTime())) return null;
  // ISO / zoned strings: take the instant's UTC wall-clock. Otherwise the string
  // was parsed in local time — keep its printed wall-clock in the UTC fields.
  const isIso = /T\d|\dZ$|[+-]\d{2}:?\d{2}$/.test(s);
  return isIso
    ? new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), d.getUTCHours(), d.getUTCMinutes(), d.getUTCSeconds()))
    : new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), d.getHours(), d.getMinutes(), d.getSeconds()));
}

function inferType(typeStr) {
  const s = String(typeStr || '').toLowerCase();
  if (/mms/.test(s)) return 'mms';
  if (/sms|text|message/.test(s)) return 'sms';
  if (/data|gprs|lte|pdp|internet|web/.test(s)) return 'data';
  if (/voice|call|moc|mtc|vmo|vmt|volte/.test(s)) return 'voice';
  return 'unknown';
}

function inferDir(typeStr, fromRaw, subNum, dirStr) {
  if (/\bmo\b|-mo\b|moc\b|vmo\b|originating|sms-mo|voice-mo/.test(String(typeStr).toLowerCase())) return 'outgoing';
  if (/\bmt\b|-mt\b|mtc\b|vmt\b|terminating|sms-mt|voice-mt/.test(String(typeStr).toLowerCase())) return 'incoming';
  if (dirStr) {
    const d = String(dirStr).toLowerCase();
    if (/out|orig|\bmo\b|send/.test(d)) return 'outgoing';
    if (/in|term|\bmt\b|recv|receiv/.test(d)) return 'incoming';
  }
  if (subNum && fromRaw && normNum(fromRaw) === normNum(subNum)) return 'outgoing';
  if (subNum && fromRaw && normNum(fromRaw) !== normNum(subNum) && normNum(fromRaw).length >= 10) return 'incoming';
  return 'unknown';
}

function parseDur(v, isDurationInMinutes) {
  const n = parseFloat(String(v || '').replace(/[^0-9.]/g, ''));
  if (isNaN(n)) return 0;
  return isDurationInMinutes ? Math.round(n * 60) : Math.round(n);
}

// ── T-Mobile File Processor ─────────────────────────────────────────
//
// A T-Mobile legal-demand return is one or more files per queried number:
// call/SMS/data detail files (parsed into records below) plus an optional
// "SUB_..." subscriber-information export (name/address/account/device —
// parsed separately below, never fed through the record parser). Both CSV
// and Excel (.xlsx) exports share the same preamble ("Provided by T-Mobile
// On:" / "Tracking ID:" / etc. label rows before the real header row).

const isBigInteger = v => typeof v === 'number' && Number.isInteger(v) && !Number.isSafeInteger(v);

function colLetters(n) {
  let s = '';
  for (; n > 0; n = Math.floor((n - 1) / 26)) s = String.fromCharCode(65 + ((n - 1) % 26)) + s;
  return s;
}

// exceljs reads number cells into JS numbers, which drops the last digits of
// anything past 2^53 — e.g. T-Mobile's 17-digit 5G cell IDs, where adjacent
// sectors differ only in those digits. Read such cells' exact text from the
// first sheet's XML instead (cell ref → digits).
async function exactBigNumbers(buf) {
  const { default: JSZip } = await import('jszip');
  const zip = await JSZip.loadAsync(buf);
  const wbXml = await zip.file('xl/workbook.xml')?.async('string');
  const relsXml = await zip.file('xl/_rels/workbook.xml.rels')?.async('string');
  const rid = wbXml && (wbXml.match(/<sheet\b[^>]*\br:id="([^"]+)"/) || [])[1];
  const rel = rid && relsXml && (relsXml.match(/<Relationship\b[^>]*>/g) || []).find(t => t.includes(`Id="${rid}"`));
  const target = rel && (rel.match(/\bTarget="([^"]+)"/) || [])[1];
  if (!target) return new Map();
  const path = target.startsWith('/') ? target.slice(1) : `xl/${target.replace(/^\.\//, '')}`;
  const sheetXml = await zip.file(path)?.async('string');
  const exact = new Map();
  for (const m of (sheetXml || '').matchAll(/<c\b([^>]*)>\s*<v>(-?\d{16,})<\/v>/g)) {
    const ref = (m[1].match(/\br="([A-Z]+\d+)"/) || [])[1];
    if (ref) exact.set(ref, m[2]);
  }
  return exact;
}

async function readTMobileTable(file) {
  let allRows = [];

  if (/\.xlsx?$/i.test(file.name)) {
    const { default: ExcelJS } = await import('exceljs');
    const buf = await file.arrayBuffer();
    const wb = new ExcelJS.Workbook();
    await wb.xlsx.load(buf);
    const ws = wb.worksheets[0];
    let hasBig = false;
    ws.eachRow(row => { if (!hasBig && row.values.some(isBigInteger)) hasBig = true; });
    const exact = hasBig ? await exactBigNumbers(buf) : new Map();
    // includeEmpty keeps row position aligned with the sheet's visual
    // layout (the label-row preamble includes fully blank rows) so the
    // header-row search below lines up the same way it does for CSV.
    ws.eachRow({ includeEmpty: true }, (row, rowNumber) => {
      allRows.push(row.values.slice(1).map((v, j) => {
        if (v == null) return '';
        if (v instanceof Date) return v.toISOString();
        if (typeof v === 'object') return String(v.text || v.result || '');
        if (isBigInteger(v)) return exact.get(`${colLetters(j + 1)}${rowNumber}`) || String(v);
        return String(v);
      }));
    });
  } else {
    const text = await file.text();
    allRows = parseCSVText(text);
    if (!allRows.length) throw new Error('File appears to be empty.');
  }

  // T-Mobile's exports lead with several "Provided by T-Mobile On:" /
  // "Tracking ID:" / etc. label rows before the real column-header row —
  // find the first row that looks like a header instead of assuming row 1.
  // (Timing-advance exports run to six label lines plus blanks, so look a
  // little further than the CDR exports need.)
  let hi = 0;
  for (let i = 0; i < Math.min(allRows.length, 25); i++) {
    if (allRows[i].filter(v => v).length >= 3) { hi = i; break; }
  }
  const headers = allRows[hi].map(h => String(h || '').trim());
  const rawRows = allRows.slice(hi + 1).filter(r => r.some(c => c !== ''));
  const preamble = allRows.slice(0, hi).filter(r => r.some(c => c !== ''));

  return { headers, rawRows, preamble };
}

// SUB_ subscriber-info exports carry no call/text/data rows — routing them
// through the record parser would misread label columns as CDR fields.
// Detect by T-Mobile's filename convention first, falling back to the
// header row's own columns in case a file gets renamed.
function classifyTMobileKind(fileName, headers) {
  const hl = headers.map(h => String(h || '').toLowerCase().trim());
  if (/^sub[_-]/i.test(fileName) || hl.includes('subscriber first name') || hl.includes('billing account number (ban)')) {
    return 'subscriber';
  }
  if (hl.some(h => /timing[\s_]*advance/.test(h))) return 'timing-advance';
  return 'cdr';
}

// Some T-Mobile CDR exports (e.g. the Calling/Dialed/Called/Destination
// Number + Completion Code voice report) carry no per-row "type" column at
// all — the column set itself identifies the record kind.
function tmobileDefaultType(headers) {
  const joined = headers.map(h => String(h || '').toLowerCase()).join(' ');
  if (/completion code|calling number|dialed number|switch name/.test(joined)) return 'voice';
  if (/\bmms\b/.test(joined)) return 'mms';
  if (/\bsms\b|message/.test(joined)) return 'sms';
  if (/\bdata\b|bytes|kilobytes|megabytes|\bapn\b/.test(joined)) return 'data';
  return '';
}

function parseTMobileRecords(headers, rawRows) {
  const carrier = 'tmobile';
  const fm = detectFields(headers, carrier);
  const durHeader = fm.duration !== undefined ? (headers[fm.duration] || '').toLowerCase() : '';
  const durationInMinutes = /\bmin/.test(durHeader);
  const defaultType = tmobileDefaultType(headers);

  let subNum = '';
  if (fm.from !== undefined) {
    const freq = {};
    rawRows.forEach(r => {
      const n = normNum(String(r[fm.from] || ''));
      if (n.length >= 10) freq[n] = (freq[n] || 0) + 1;
    });
    const sorted = Object.entries(freq).sort((a, b) => b[1] - a[1]);
    if (sorted.length) subNum = sorted[0][0];
  }

  const g = (row, field) => fm[field] !== undefined ? row[fm[field]] : undefined;

  const records = rawRows.map((row, idx) => {
    let ts = null;
    const dtv = g(row, 'datetime');
    if (dtv) ts = parseTs(String(dtv), '');
    if (!ts) ts = parseTs(String(g(row, 'date') || ''), String(g(row, 'time') || ''));

    const typeStr = String(g(row, 'type') || '');
    const fromRaw = String(g(row, 'from') || '');
    const toRaw   = String(g(row, 'to')   || '');
    const dirRaw  = String(g(row, 'direction') || '');

    const inferredType = inferType(typeStr);
    const type      = inferredType !== 'unknown' ? inferredType : (defaultType || 'unknown');
    const direction = inferDir(typeStr, fromRaw, subNum, dirRaw);

    let contactRaw = '';
    if (direction === 'outgoing')  contactRaw = toRaw;
    else if (direction === 'incoming') contactRaw = fromRaw;
    else {
      const fn = normNum(fromRaw), tn = normNum(toRaw), sn = normNum(subNum);
      if (fn && fn !== sn && fn.length >= 7) contactRaw = fromRaw;
      else if (tn && tn !== sn && tn.length >= 7) contactRaw = toRaw;
      else contactRaw = toRaw || fromRaw;
    }

    return {
      id: idx, timestamp: ts, direction, type,
      contactNumber: normNum(contactRaw) || contactRaw,
      durationSeconds: parseDur(g(row, 'duration'), durationInMinutes),
      cellId: String(g(row, 'cell') || ''),
      imei:   String(g(row, 'imei') || ''),
      raw: Object.fromEntries(headers.map((h, j) => [h, String(row[j] ?? '')])),
    };
  }).filter(r => r.contactNumber || r.timestamp);

  const tsList = records.map(r => r.timestamp).filter(Boolean);
  return {
    carrier, subNum, records, headers,
    minDate: tsList.length ? new Date(Math.min(...tsList.map(t => t.getTime()))) : null,
    maxDate: tsList.length ? new Date(Math.max(...tsList.map(t => t.getTime()))) : null,
  };
}

// SUB_ export: one row per line/account-holder change over time (e.g. a
// prior subscriber followed by the current one on the same MSISDN) — the
// row with an Active status is treated as current; IMEIs/devices are
// collected across every row so device history isn't lost.
function parseTMobileSubscriberInfo(headers, rawRows) {
  const hl = headers.map(h => String(h || '').toLowerCase().trim());
  const idx = (...names) => {
    for (const n of names) {
      const i = hl.findIndex(h => h === n.toLowerCase());
      if (i >= 0) return i;
    }
    return -1;
  };
  const g = (row, i) => (i >= 0 ? String(row[i] ?? '').trim() : '');

  const iSubFirst     = idx('subscriber first name');
  const iSubMiddle    = idx('subscriber middle name');
  const iSubLast      = idx('subscriber last name');
  const iSubAddr      = idx('subscriber address');
  const iSubStatus    = idx('subscriber status');
  const iAcctSsn      = idx('account ssn');
  const iAcctDob      = idx('account date of birth');
  const iAcctAddr     = idx('account address');
  const iBan          = idx('billing account number (ban)', 'billing account number');
  const iMsisdn       = idx('msisdn');
  const iMsisdnStatus = idx('msisdn status');
  const iDeviceModel  = idx('device model');
  const iDeviceImei   = idx('device imei');

  const info = {
    name: '', address: '', city: '', state: '', zip: '', dob: '', ssn: '',
    accountNumber: '', mtn: '', imeis: [], devices: [], emails: [], otherNumbers: [],
  };

  const activeRow = rawRows.find(r => /active/i.test(g(r, iMsisdnStatus)) || /active/i.test(g(r, iSubStatus)));
  const primary = activeRow || rawRows[rawRows.length - 1];

  if (primary) {
    info.name = [g(primary, iSubFirst), g(primary, iSubMiddle), g(primary, iSubLast)].filter(Boolean).join(' ');
    info.address = g(primary, iSubAddr) || g(primary, iAcctAddr);
    info.ssn = g(primary, iAcctSsn);
    info.dob = g(primary, iAcctDob);
    info.accountNumber = g(primary, iBan);
    info.mtn = normNum(g(primary, iMsisdn));
  }

  rawRows.forEach(row => {
    const imei = g(row, iDeviceImei);
    if (imei && !info.imeis.includes(imei)) { info.imeis.push(imei); info.devices.push(g(row, iDeviceModel)); }
  });

  return info;
}

// ── T-Mobile Timing Advance ("Timing Advance by User") ───────────────
//
// One row per network session (not a call or text, so these are returned on
// their own as `timingAdvance`, never mixed into the call records): start and
// end time, the serving cell at each end (cell, eNodeB/gNodeB, TAC/LAC,
// sector), and the timing-advance distance from the cell that measured it
// (Start/End_Timing_Advance_<unit>, from Start/End_Timing_Advance_Cell).
// End_Latitude/End_Longitude and Confidence are T-Mobile's own location for
// the session's end, kept as given. Times are UTC per the return's preamble.

const TA_UNITS = [
  [/^(mi|mile|miles)$/, 'mi', 1609.344],
  [/^(km|kms|kilometers?|kilometres?)$/, 'km', 1000],
  [/^(m|meters?|metres?|mtrs?)$/, 'm', 1],
  [/^(ft|feet)$/, 'ft', 0.3048],
];

// The unit is the header's last word ("Start_Timing_Advance_Miles"). An
// unrecognized word is kept as the label, with no conversion to meters.
function taUnit(header) {
  const word = String(header || '').replace(/^.*timing[\s_]*advance[\s_]*/i, '').replace(/[()[\]]/g, '').trim();
  const hit = TA_UNITS.find(([re]) => re.test(word.toLowerCase()));
  return hit ? { unit: hit[1], unitLabel: word, toMeters: hit[2] } : { unit: '', unitLabel: word, toMeters: null };
}

// The queried number: a "MSISDN: 5550100003"-style preamble line, or the
// 10-digit group in T-Mobile's file name (XX_X_5550100003_...).
function tmobileQueriedNumber(fileName, preamble) {
  for (const row of preamble || []) {
    const line = row.filter(Boolean).join(' ').trim();
    if (/tracking|case/i.test(line)) continue;
    const m = line.match(/^[^:]{1,30}:\s*\+?1?[\s(-]*(\d{3})\D{0,2}(\d{3})\D?(\d{4})$/);
    if (m) return m[1] + m[2] + m[3];
  }
  const f = String(fileName || '').match(/(?:^|[_\s-])1?(\d{10})(?=[_\s.-])/);
  return f ? f[1] : '';
}

function parseTMobileTimingAdvance(headers, rawRows, fileName, preamble) {
  const nh = headers.map(h => String(h || '').toLowerCase().replace(/[^a-z0-9]/g, ''));
  const find = re => nh.findIndex(h => re.test(h));
  const c = {
    endLat: find(/^endlat/), endLon: find(/^endlon|^endlng/), confidence: find(/^confidence$/),
    imsi: find(/^imsi$/), imei: find(/^imei$/), model: find(/^model$/), make: find(/^make$/),
    service: find(/^servicetype$/), market: find(/^market$/), deviceType: find(/^device/),
    start: find(/^start(date)?time$/), end: find(/^end(date)?time$/), duration: find(/^duration/),
    startCell: find(/^startcell$/), startNode: find(/^start(enodeb|gnodeb|nodeb|enb|gnb)/),
    startTacLac: find(/^start(tac|lac)/), startSector: find(/^startsector$/),
    endCell: find(/^endcell$/), endNode: find(/^end(enodeb|gnodeb|nodeb|enb|gnb)/),
    endTacLac: find(/^end(tac|lac)/), endSector: find(/^endsector$/),
    startTa: find(/^starttimingadvance(?!cell$)/), endTa: find(/^endtimingadvance(?!cell$)/),
    startTaCell: find(/^starttimingadvancecell$/), endTaCell: find(/^endtimingadvancecell$/),
  };
  const { unit, unitLabel, toMeters } = taUnit(headers[c.startTa >= 0 ? c.startTa : c.endTa]);
  const g = (row, i) => (i >= 0 ? String(row[i] ?? '').trim() : '');
  const num = (row, i) => {
    const v = g(row, i);
    const n = v === '' ? NaN : Number(v);
    return Number.isFinite(n) ? n : null;
  };
  const end = (row, pre) => {
    const distance = num(row, c[`${pre}Ta`]);
    return {
      cellId: g(row, c[`${pre}Cell`]),
      nodeId: g(row, c[`${pre}Node`]),
      tacLac: g(row, c[`${pre}TacLac`]),
      sector: g(row, c[`${pre}Sector`]),
      taCellId: g(row, c[`${pre}TaCell`]),
      taDistance: distance,
      taMeters: distance != null && toMeters ? Math.round(distance * toMeters) : null,
    };
  };

  const sessions = rawRows.map((row, idx) => {
    const lat = num(row, c.endLat), lon = num(row, c.endLon);
    return {
      id: idx,
      start: parseTs(g(row, c.start), ''),
      end: parseTs(g(row, c.end), ''),
      durationSeconds: parseDur(g(row, c.duration), false),
      serviceType: g(row, c.service),
      imsi: g(row, c.imsi),
      imei: g(row, c.imei),
      device: [g(row, c.make), g(row, c.model)].filter(Boolean).join(' '),
      deviceType: g(row, c.deviceType),
      market: g(row, c.market),
      startCell: end(row, 'start'),
      endCell: end(row, 'end'),
      endLocation: lat != null && lon != null && (lat || lon) ? { lat, lon } : null,
      confidence: g(row, c.confidence),
      distanceUnit: unit,
      distanceUnitLabel: unitLabel,
      raw: Object.fromEntries(headers.map((h, j) => [h, String(row[j] ?? '')])),
    };
  }).filter(s => s.start || s.startCell.cellId || s.endCell.cellId);

  return { subNum: tmobileQueriedNumber(fileName, preamble), sessions };
}

// ── Multi-bucket orchestration ──────────────────────────────────────────

// Process the three carrier drop-buckets (AT&T / Verizon / T-Mobile) into a
// single merged record set, tagged by carrier + subscriber, plus a
// per-response summary. Reuses the existing per-carrier parsers.
export async function processBuckets(buckets) {
  const all = [];
  const responses = {};
  const ensure = (carrier, subNum) => {
    const key = `${carrier}:${subNum || 'unknown'}`;
    if (!responses[key]) responses[key] = { key, carrier, subNum: subNum || '', subInfo: null, count: 0, fileNames: [] };
    return responses[key];
  };
  // Records already carrying their own subNum (AT&T's per-"Voice Usage For:"
  // section attribution) keep it; otherwise fall back to the file/return-level
  // default passed in. Same for sourceFile — it's normally set per-record
  // already (the actual uploaded file it came from); the passed-in default
  // only backstops records that somehow didn't get one.
  const tag = (records, carrier, subNum, sourceFile) =>
    records.map(r => ({ ...r, carrier, subNum: r.subNum || subNum || '', sourceFile: r.sourceFile || sourceFile }));

  let fileCount = 0;
  const timingAdvance = [];   // T-Mobile timing-advance sessions (not call records)

  // AT&T: one legal-demand return can cover several queried numbers, each
  // introduced by its own "Voice/Data/SMS/SMS-Email Usage For:" section (or,
  // for ReportCT/CAPM/AccountNotesSF/IMEI, its own block) — split records
  // AND subscriber profile data into a separate response per number instead
  // of collapsing them into one. PDFs are ignored inside processAttFiles.
  if (buckets.att.length) {
    fileCount += buckets.att.length;
    const data = await processAttFiles(buckets.att);
    const bySub = {};
    data.records.forEach(r => {
      const sn = r.subNum || data.subNum || '';
      (bySub[sn] = bySub[sn] || []).push(r);
    });
    const allNums = new Set([...Object.keys(bySub), ...Object.keys(data.infoByNumber)]);
    if (!allNums.size) allNums.add(data.subNum || '');
    allNums.forEach(sn => {
      all.push(...tag(bySub[sn] || [], 'att', sn, 'AT&T'));
      const resp = ensure('att', sn);
      resp.fileNames.push(...(data.fileNames || []));
      if (data.infoByNumber[sn]) resp.subInfo = data.infoByNumber[sn];
    });
  }

  // Verizon: each file is queried against one subject number encoded in its
  // own filename (extractVzwFileNumber) — split records AND subscriber-info
  // into a separate response per number, same as AT&T above, so several
  // people's Verizon returns can be uploaded together.
  if (buckets.verizon.length) {
    fileCount += buckets.verizon.length;
    const entries = buckets.verizon.map(f => classifyVzwFile(f));
    const data = await processVerizonFiles(entries);
    const bySub = {};
    data.records.forEach(r => {
      const sn = r.subNum || '';
      (bySub[sn] = bySub[sn] || []).push(r);
    });
    const allNums = new Set([...Object.keys(bySub), ...Object.keys(data.infoByNumber), ...Object.keys(data.fileNamesByNumber)]);
    if (!allNums.size) allNums.add('');
    allNums.forEach(sn => {
      all.push(...tag(bySub[sn] || [], 'verizon', sn, 'Verizon'));
      const resp = ensure('verizon', sn);
      resp.fileNames.push(...(data.fileNamesByNumber[sn] || []));
      if (data.infoByNumber[sn]) resp.subInfo = data.infoByNumber[sn];
    });
  }

  // T-Mobile: one CDR file = one subpoena response; a "SUB_" file in the
  // same bucket carries subscriber info for that response instead of call/
  // text/data records, and is merged into the matching response by MSISDN.
  // PDFs are ignored.
  for (const file of buckets.tmobile) {
    if (/\.pdf$/i.test(file.name)) continue;
    fileCount++;
    try {
      const { headers, rawRows, preamble } = await readTMobileTable(file);
      if (!rawRows.length) continue;
      const kind = classifyTMobileKind(file.name, headers);
      if (kind === 'subscriber') {
        const info = parseTMobileSubscriberInfo(headers, rawRows);
        const resp = ensure('tmobile', info.mtn);
        resp.subInfo = resp.subInfo ? { ...resp.subInfo, ...info } : info;
        resp.fileNames.push(file.name);
      } else if (kind === 'timing-advance') {
        const data = parseTMobileTimingAdvance(headers, rawRows, file.name, preamble);
        timingAdvance.push(...data.sessions.map(s => ({ ...s, carrier: 'tmobile', subNum: data.subNum, sourceFile: file.name })));
        const resp = ensure('tmobile', data.subNum);
        resp.timingAdvanceCount = (resp.timingAdvanceCount || 0) + data.sessions.length;
        resp.fileNames.push(file.name);
      } else {
        const data = parseTMobileRecords(headers, rawRows);
        const tagged = tag(data.records, 'tmobile', data.subNum, file.name);
        all.push(...tagged);
        const resp = ensure('tmobile', data.subNum);
        resp.fileNames.push(file.name);
      }
    } catch (e) {
      console.error(`Failed to parse ${file.name}:`, e);
    }
  }

  // Duplicate records — the same file uploaded twice, or two overlapping-
  // date-range returns for the same subject re-covering the same calls —
  // are identified by every field that describes the same real-world event
  // (who, what, when, how long). Flagged rather than dropped here, so the
  // dashboard can toggle deduplication on/off instantly without
  // reprocessing the uploaded files. When a timestamp failed to parse, fall
  // back to the full raw row instead of treating every un-timestamped
  // record as equivalent, so two different events that both merely lack a
  // timestamp aren't wrongly flagged as duplicates of each other.
  const seen = new Set();
  let duplicatesRemoved = 0;
  all.forEach(r => {
    const key = [
      r.carrier, r.subNum || '', r.type, r.direction, r.contactNumber || '',
      r.timestamp ? r.timestamp.getTime() : `raw:${JSON.stringify(r.raw || {})}`,
      r.durationSeconds || 0,
    ].join('|');
    r.isDuplicate = seen.has(key);
    if (r.isDuplicate) duplicatesRemoved++;
    else seen.add(key);
  });
  all.forEach((r, i) => { r.id = i; });

  // Same idea for timing-advance sessions (the same file uploaded twice).
  const seenTa = new Set();
  timingAdvance.forEach((s, i) => {
    const key = [
      s.subNum, s.start ? s.start.getTime() : `raw:${JSON.stringify(s.raw)}`, s.end ? s.end.getTime() : '',
      s.startCell.cellId, s.endCell.cellId, s.imsi,
    ].join('|');
    s.isDuplicate = seenTa.has(key);
    seenTa.add(key);
    s.id = i;
  });

  const tsAll = all.map(r => r.timestamp).filter(Boolean);
  return {
    records: all,
    timingAdvance,
    responses: Object.values(responses),
    fileCount,
    duplicatesRemoved,
    minDate: tsAll.length ? new Date(Math.min(...tsAll.map(t => t.getTime()))) : null,
    maxDate: tsAll.length ? new Date(Math.max(...tsAll.map(t => t.getTime()))) : null,
  };
}
